import {useIsFeatureActive} from '@aboutyou/feature-flags';
import {SizeSelection_Size} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/size_selection.types';
import {GetProductBulkResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/article_detail_page/v1/service.types';
import {useTranslation} from '@aboutyou/i18n';
import {TrackingRecoElement} from '@aboutyou/legacy-tracking-sdk';
import {
  COLORS,
  ContentPopper,
  ContentPopperProps,
  SPACING,
  SizeSelector,
  Text,
  mobileOnlyLinaria,
} from '@aboutyou/ui-components';
import {AddToBasketTrackingData, BasketMutationSuccess} from '@aboutyou/user';
import {styled} from '@linaria/react';
import React, {useCallback, useMemo, useState} from 'react';
import {useAddToBasketTrackingData} from '../../../features/ProductTile/hooks/useAddToBasketTrackingData';
import {SizeHeader} from '../../../features/SizeSelection/SizeHeader';
import {SizeSection} from '../../../features/SizeSelection/SizeSection';
import {
  SizeSectionSizeReminder,
  useSizeReminderState,
} from '../../../features/SizeSelection/SizeSectionSizeReminder';
import {
  SoldOutSizeSelectionModal,
  SoldOutSizeSelectionProps,
} from '../../../features/SizeSelection/SoldOutSizeSelection';
import {getSizeByIdOrLabel} from '../../../features/SizeSelection/getSizeByIdOrLabel';
import {useNormalizedSizes} from '../../../features/SizeSelection/helpers';
import {usePreselectSize} from '../../../features/SizeSelection/hooks/usePreselectSize';
import {useRecommendedSize} from '../../../features/SizeSelection/hooks/useRecommendedSize';
import {
  SelectedSize,
  SetSelectedSize,
} from '../../../features/SizeSelection/hooks/useSelectedSizeState';
import {useIsSingleSizeSelection} from '../../../features/SizeSelection/utils/useIsSingleSizeSelection';
import {AddToBasketFlyout} from '../../../features/SizeSelectionFlyout/AddToBasketFlyout';
import {AddToBasketOnSuccessProps} from '../../../features/SizeSelectionFlyout/hooks/useAddToBasket';
import {
  SizeSelectionOpenedStateKey,
  useSizeSelectionOpenedState,
} from '../../../features/SizeSelectionFlyout/hooks/useSizeSelectionOpenedState';
import {AvailabilitySectionView} from '../AvailabilitySection/AvailabilitySectionView';
import {AddToWishlistButton} from '../ProductSlider/CTAs/AddToWishlistButton';
import {useGetBadgeListKeys} from '../utils/useGetBadgeListKeys';
import {AddToBasket} from './AddToBasket/AddToBasket';
import {SizeAdvisorSection} from './SizeAdvisorSection/SizeAdvisorSection';
import {getSizeFinderRecommendations} from './getSizeFinderRecommendations';

export interface AddToBasketSectionViewProps {
  selectedSize: SelectedSize;
  setSelectedSize: SetSelectedSize;
  SizeSelectionLayoutComponent: ContentPopperProps['LayoutComponent'];
  showPriceInSizeSelectionHeader: boolean;
  SizeReminderLayoutComponent: ContentPopperProps['LayoutComponent'];
  className?: string;
  showWishlistButton: boolean;
  onAddToBasketSuccess?: (props: AddToBasketOnSuccessProps) => void;
  disableBasketRecommendations?: boolean;
  showSizeAdvisor?: boolean;
  filteredSizeId?: number;
  withFlyout?: boolean;
  renderStickySection?: boolean;
  showStickySection?: boolean;
  shouldUseLightBorder?: boolean;
  shouldUseSquareWishButton?: boolean;
  shouldDisplaySizeAdvisorAboveSizeSelection?: boolean;
  shouldOptimiseElementsSpacing?: boolean;
  sizeSelectionStateManagerKey: SizeSelectionOpenedStateKey;
  shouldDisplaySizeAdvisorBelowSizeSelection?: boolean;
  enableSelectedYourSizeGreyBadges?: boolean;
  trackingData?: {
    sectionIndex?: number;
    sortingChannel?: string;
    pageType?: string;
    recoType?: string;
    elements?: TrackingRecoElement[];
  };
  isInMiniADPDialog: boolean;
  SoldOutSizeReminderComponent?: React.FC<SoldOutSizeSelectionProps>;
  renderSoldoutSelectionAsDropdown?: boolean;
  overrideBETrackingDataState?: AddToBasketTrackingData;
  productId?: number;
}

// eslint-disable-next-line complexity -- the complexity was increased by the running A/B tests, this will be fixed after the A/B test is over
export const AddToBasketSectionView = ({
  brand,
  product,
  sizeSelection,
  selectedSize,
  setSelectedSize,
  saleBadges,
  SizeSelectionLayoutComponent,
  showPriceInSizeSelectionHeader,
  SizeReminderLayoutComponent,
  className,
  showWishlistButton,
  onAddToBasketSuccess,
  showSizeAdvisor = true,
  filteredSizeId,
  withFlyout,
  shouldUseLightBorder = false,
  shouldUseSquareWishButton = false,
  shouldDisplaySizeAdvisorAboveSizeSelection = false,
  shouldOptimiseElementsSpacing = false,
  sizeSelectionStateManagerKey,
  shouldDisplaySizeAdvisorBelowSizeSelection,
  price,
  enableSelectedYourSizeGreyBadges,
  trackingData,
  isInMiniADPDialog,
  SoldOutSizeReminderComponent,
  renderSoldoutSelectionAsDropdown,
  overrideBETrackingDataState,
  productId,
  ref,
}: GetProductBulkResponse['sizesSection'] &
  AddToBasketSectionViewProps & {ref?: React.Ref<HTMLElement>}) => {
  const {t} = useTranslation();
  const {
    isOpen: isSizeSelectionOpen,
    cancel: cancelSizeSelection,
    hide: hideSizeSelection,
    open: openSizeSelection,
  } = useSizeSelectionOpenedState(sizeSelectionStateManagerKey);
  const {isActive: enablePriceOverhaulV2} = useIsFeatureActive(
    'ab_price_display_overhaul_v2',
  );
  const {badgesListKeys} = useGetBadgeListKeys({
    isInMiniADPDialog,
    productId,
  });
  const addToBasketTrackingDataFromRouteState = useAddToBasketTrackingData();
  const addToBasketTrackingData =
    overrideBETrackingDataState ?? addToBasketTrackingDataFromRouteState;

  const normalizedSize = useNormalizedSizes([selectedSize!], {
    enableYourSizeGreyBadges: enableSelectedYourSizeGreyBadges,
    enablePriceOverhaulV2,
  });

  const sizeSelectiontype = sizeSelection.type;
  const isOneSizeSelection = sizeSelectiontype?.$case === 'oneSize';
  const {
    showSizeReminder,
    sizeReminderSize,
    sizeReminderSizeIds,
    toggleSoldOutSizeId,
    resetSizeReminderSelection,
  } = useSizeReminderState(
    sizeSelectiontype?.$case === 'oneSize' && sizeSelectiontype.oneSize.size
      ? [sizeSelectiontype.oneSize.size.sizeId]
      : [],
  );
  const [isOpenSizeReminder, setIsOpenSizeReminder] = useState(false);
  const isSingleSizeProvided = useIsSingleSizeSelection(sizeSelection.type);
  const isSingleSizeInMiniADP = Boolean(
    isInMiniADPDialog && !isSingleSizeProvided,
  );

  const onSizeReminderShowChange = (size: SizeSelection_Size) => {
    showSizeReminder(size);
    setIsOpenSizeReminder(true);
  };

  const shouldUseSizeFinderAdviceRef = React.useRef(false);

  const recommendedSize = useRecommendedSize({
    sizeSelection,
    productId: product.id,
    shouldUseSizeFinderAdviceRef,
  });

  const shouldShowSizeAdvisor =
    showSizeAdvisor && sizeSelection.displaySizeAdviser;

  const onSizeAdvisorSizeSelection = useCallback(
    (sizeId: number) => {
      const selectedSizeFromSizeAdvisor = getSizeByIdOrLabel({
        sizeSelection,
        sizeIdentifier: {type: 'id', id: sizeId.toString()},
      });
      setSelectedSize(selectedSizeFromSizeAdvisor);
    },
    [setSelectedSize, sizeSelection],
  );

  usePreselectSize({
    productId: product.id,
    setSelectedSize,
    sizeSelection,
    filteredSizeId,
    recommendedSize,
    isSizeSelectionOpen,
    selectedSizeId: selectedSize?.sizeId,
  });

  const sizeFinderRecommendationsCallback = useMemo(
    () => getSizeFinderRecommendations(sizeSelection),
    [sizeSelection],
  );

  if (!sizeSelection) {
    return null;
  }

  if (product?.isSoldOut) {
    const SoldOutSizeSelection =
      SoldOutSizeReminderComponent ?? SoldOutSizeSelectionModal;
    return (
      <SoldOutSizeSelection
        sizeSelection={sizeSelection}
        selectedSizeIds={sizeReminderSizeIds}
        onSizeSelect={toggleSoldOutSizeId}
        product={product}
        sizeReminderSizeIds={sizeReminderSizeIds}
        resetSizeSelection={resetSizeReminderSelection}
        renderAsDropdown={renderSoldoutSelectionAsDropdown}
      />
    );
  }

  const handleOpenerClick = () => {
    openSizeSelection();
  };

  const handleOpenerClose = () => {
    cancelSizeSelection();
  };

  const releaseOneSizeSelection = () => {
    if (isOneSizeSelection) {
      hideSizeSelection();
    }
  };

  const handleAddToBasketSuccess = ({
    statusSuccessSections,
    freeDeliverySection,
  }: Omit<BasketMutationSuccess, 'failed'>) => {
    onAddToBasketSuccess?.({freeDeliverySection, statusSuccessSections});
  };

  return (
    <section ref={ref} className={className}>
      {sizeSelection.type?.$case !== 'oneSize' && (
        <>
          {shouldDisplaySizeAdvisorAboveSizeSelection &&
            !shouldDisplaySizeAdvisorBelowSizeSelection && (
              <StyledAdvisorSectionAboveSizeSelection>
                <Text size="px14">{t('Size')}</Text>
                {shouldShowSizeAdvisor && (
                  <StyledSizeAdvisorSection
                    product={product}
                    sizeSelection={sizeSelection}
                    onSizeSelect={onSizeAdvisorSizeSelection}
                  />
                )}
              </StyledAdvisorSectionAboveSizeSelection>
            )}
          <StyledContentPopper
            LayoutComponent={SizeSelectionLayoutComponent}
            data-testid="sizeFlyout"
            openerContent={
              <SizeSelector
                selection={normalizedSize.find(
                  size => size.id === selectedSize?.sizeId,
                )}
              />
            }
            onOpenerClick={handleOpenerClick}
            onClose={withFlyout ? () => void 0 : handleOpenerClose}
            open={withFlyout ? false : isSizeSelectionOpen}
            centerDropDownContent={isSingleSizeInMiniADP}
            forceContentOpenerGreyBorder={isSingleSizeInMiniADP}
            openerType={isSingleSizeInMiniADP ? 'caret' : 'single-arrow'}
            forceContentOpenerIconColor={
              isSingleSizeInMiniADP ? 'Grey1' : undefined
            }
            headerContent={
              !withFlyout &&
              showPriceInSizeSelectionHeader && (
                <SizeHeader
                  product={product}
                  saleBadges={saleBadges}
                  productPrice={price}
                />
              )
            }
            bodyContent={
              !withFlyout && (
                <SizeSection
                  product={product}
                  onSizeSelect={size => {
                    setSelectedSize(size);

                    cancelSizeSelection();
                  }}
                  selectedSize={selectedSize}
                  sizeSelection={sizeSelection}
                  showSizeReminder={onSizeReminderShowChange}
                  setContentFullHeight={isInMiniADPDialog}
                />
              )
            }
          />
        </>
      )}

      {/* For one-size products the flyout renders no size grid: mounting it
          triggers its auto-add effect (and the captcha flyout when needed),
          so it must only mount while the size selection state is open, and
          the opened state must be released again once the add settles. */}
      {withFlyout && (!isOneSizeSelection || isSizeSelectionOpen) && (
        <AddToBasketFlyout
          isOpen={isSizeSelectionOpen}
          withSuccessToast={false}
          withErrorToast
          sizeSelectionData={{
            sizeSelection,
            product,
            brand,
            saleBadges,
          }}
          product={product}
          onClose={handleOpenerClose}
          preselectedSize={selectedSize}
          onSuccess={({size, freeDeliverySection, statusSuccessSections}) => {
            setSelectedSize(size);

            releaseOneSizeSelection();

            handleAddToBasketSuccess({
              freeDeliverySection,
              statusSuccessSections,
            });
          }}
          onError={error => {
            if (!error?.captchaFailed) {
              releaseOneSizeSelection();
            }
          }}
          onCaptchaClose={releaseOneSizeSelection}
          onSizeAdvisorSizeSelection={onSizeAdvisorSizeSelection}
          disableCouponsSlider
          overrideBETrackingData={addToBasketTrackingData}
          badgesListKeys={badgesListKeys}
        />
      )}

      <SizeSectionSizeReminder
        brand={brand}
        product={product!}
        SizeReminderLayoutComponent={SizeReminderLayoutComponent}
        sizeReminderSize={sizeReminderSize}
        onResetSizeReminderSize={() => setIsOpenSizeReminder(false)}
        sizeSelectionOpenedStateKey={sizeSelectionStateManagerKey}
        isOpen={isOpenSizeReminder}
      />

      {!shouldDisplaySizeAdvisorAboveSizeSelection &&
        !shouldDisplaySizeAdvisorBelowSizeSelection &&
        shouldShowSizeAdvisor && (
          <StyledSizeAdvisorSection
            $addTopMargin
            product={product}
            sizeSelection={sizeSelection}
            onSizeSelect={onSizeAdvisorSizeSelection}
            id={`${sizeSelectionStateManagerKey}-sizeAdvisor`}
          />
        )}

      <AvailabilityNoteSection
        $shouldUseMarginTop
        selectedSize={selectedSize}
        defaultHint={sizeSelection.defaultLowStockHint}
      />

      <AddToBasket
        sizeFinderRecommendationsCallback={sizeFinderRecommendationsCallback}
        size={
          sizeSelection.type?.$case === 'oneSize'
            ? sizeSelection.type.oneSize.size
            : selectedSize
        }
        product={product}
        brandName={brand.name}
        onAddToBasketSuccess={handleAddToBasketSuccess}
        recommendedSize={recommendedSize}
        // According to BE inlineSelection is the size type use for Beauty products
        // So far the only way to know if a product is a beauty product :)
        inlineSizeSelection={sizeSelection.type?.$case === 'inlineSelection'}
        wishlistComponent={
          showWishlistButton && (
            <StyledAddToWishlistButton
              $shouldOptimiseElementsSpacing={shouldOptimiseElementsSpacing}
              $shouldUseLightBorder={shouldUseLightBorder}
              product={product}
              price={price}
              brand={brand}
              shouldUseSquareWishButton={shouldUseSquareWishButton}
              trackingData={{
                ...trackingData,
                pageType: 'adp',
                recoType: 'product',
                elements: [{id: NaN, type: 'product'}],
              }}
              badgesListKeys={badgesListKeys}
            />
          )
        }
        sizeSelectionOpenedStateKey={sizeSelectionStateManagerKey}
        isOneSize={sizeSelection.type?.$case === 'oneSize'}
        trackingData={{...trackingData, brandName: brand.name}}
        overrideBETrackingData={addToBasketTrackingData}
        badgesList={badgesListKeys}
      />

      {shouldDisplaySizeAdvisorBelowSizeSelection && shouldShowSizeAdvisor && (
        <StyledSizeAdvisorSection
          $addTopMargin
          product={product}
          sizeSelection={sizeSelection}
          onSizeSelect={onSizeAdvisorSizeSelection}
        />
      )}
    </section>
  );
};

const StyledAdvisorSectionAboveSizeSelection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${SPACING['Y.Md']};
`;

const StyledSizeAdvisorSection = styled(SizeAdvisorSection)<{
  $addTopMargin?: boolean;
}>`
  margin-top: ${props => (props.$addTopMargin ? SPACING['Y.Md'] : '0')};

  ${mobileOnlyLinaria(`
    & > button {
      margin-left: 0;
    }
  `)}
`;

const AvailabilityNoteSection = styled(AvailabilitySectionView)<{
  $shouldUseMarginTop?: boolean;
}>`
  ${mobileOnlyLinaria('width: 100%;')}
  margin-top: ${props => (props.$shouldUseMarginTop ? '1rem' : '0')};
`;

const StyledAddToWishlistButton = styled(AddToWishlistButton)<{
  $shouldUseLightBorder: boolean;
  $shouldOptimiseElementsSpacing: boolean;
}>`
  &&& {
    box-shadow: none !important;
    margin-left: 10px;

    margin-top: ${props =>
      props.$shouldOptimiseElementsSpacing ? '0' : '2rem !important'};

    border: ${props =>
      props.$shouldUseLightBorder ? `solid 1px ${COLORS.Grey3}` : 'unset'};
  }
`;

const StyledContentPopper = styled(ContentPopper)`
  header {
    border-bottom-color: ${COLORS.Grey4};
  }
`;
